import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from '../ui/ThemeToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          isScrolled
            ? 'bg-white/85 dark:bg-[#060A12]/85 backdrop-blur-xl shadow-[0_8px_32px_-8px_rgba(15,23,42,0.12)] border-b border-slate-200/80 dark:border-white/[0.08]'
            : 'bg-white/75 dark:bg-[#060A12]/70 backdrop-blur-lg border-b border-slate-200/50 dark:border-white/[0.06]'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Brand Logo */}
            <Link to="/" className="flex items-center gap-3 xl:gap-3.5 group flex-shrink-0 whitespace-nowrap">
              <div className="relative flex-shrink-0">
                <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500/25 to-cyan-500/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <img
                  src="/images/ui/smc-consulting-logo.png"
                  alt="SMC Consulting Logo – Digitale Transformation und KI-Beratung"
                  className="relative h-9 lg:h-10 xl:h-11 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="hidden sm:block">
                <div className="flex items-center gap-2">
                  <span className="text-base xl:text-lg font-bold tracking-tight text-slate-900 dark:text-white whitespace-nowrap">
                    SMC Consulting
                  </span>
                  <span className="hidden xl:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                    AI & Growth
                  </span>
                </div>
                <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 tracking-wide whitespace-nowrap">
                  Executive Digital Transformation
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 bg-slate-100/70 dark:bg-white/[0.04] p-1 xl:p-1.5 rounded-2xl border border-slate-200/70 dark:border-white/[0.07] flex-shrink-0">
              {/* Services Mega Menu Trigger */}
              <div className="relative group">
                <Link
                  to="/leistungen"
                  className={`text-xs xl:text-sm font-semibold transition-all duration-200 flex items-center gap-1 xl:gap-1.5 px-2.5 xl:px-4 py-2 rounded-xl whitespace-nowrap ${
                    isActive('/leistungen')
                      ? 'text-emerald-600 dark:text-emerald-400 bg-white dark:bg-slate-800/90 shadow-sm'
                      : 'text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white/60 dark:hover:bg-white/[0.05]'
                  }`}
                >
                  <span>Leistungen</span>
                  <i className="ri-arrow-down-s-line text-sm transition-transform duration-300 group-hover:rotate-180" />
                </Link>

                {/* Hover Bridge */}
                <div className="absolute left-0 right-0 h-4 top-full" />

                {/* Executive Mega Menu Dropdown */}
                <div className="fixed top-[76px] left-1/2 -translate-x-1/2 w-[94vw] max-w-6xl bg-white/95 dark:bg-[#0B1120]/95 backdrop-blur-2xl rounded-3xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.28)] border border-slate-200/90 dark:border-white/10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-3 z-50 overflow-hidden">
                  {/* Top Specular Hairline */}
                  <div className="absolute inset-x-16 top-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

                  <div className="relative p-8">
                    {/* Mega Menu Header */}
                    <div className="flex items-center justify-between mb-7 pb-5 border-b border-slate-200/80 dark:border-white/[0.08]">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                          <i className="ri-cpu-line text-white text-xl" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2.5">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                              Lösungs-Architektur & Beratung
                            </h3>
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              BAFA-förderfähig
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            End-to-End KI-Implementierung, High-Conversion Webdesign & strategische Skalierung
                          </p>
                        </div>
                      </div>
                      <Link
                        to="/leistungen"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-500 px-3.5 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 transition-colors"
                      >
                        <span>Alle Leistungen im Überblick</span>
                        <i className="ri-arrow-right-up-line text-sm" />
                      </Link>
                    </div>

                    {/* 3-Column Services Bento Grid */}
                    <div className="grid grid-cols-3 gap-6">
                      {/* Column 1: KI & Automatisierung */}
                      <div className="space-y-3">
                        <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400 px-3">
                          01 // KI & Automatisierung
                        </div>
                        <Link
                          to="/leistungen/ki-automatisierung"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-cpu-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              KI-Prozessautomatisierung
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              Workflow-Intelligence & autonome Agenten
                            </div>
                          </div>
                        </Link>
                        <Link
                          to="/leistungen/ki-chatbots"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-robot-2-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              KI-Chatbots & Voice AI
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              RAG-Wissenssysteme & 24/7 Kundendialog
                            </div>
                          </div>
                        </Link>
                      </div>

                      {/* Column 2: Digital Experience & Growth */}
                      <div className="space-y-3">
                        <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400 px-3">
                          02 // Software & Digital Experience
                        </div>
                        <Link
                          to="/leistungen/softwareentwicklung"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-code-s-slash-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              Softwareentwicklung
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              Individuelle Web-Apps, SaaS & B2B-Portale
                            </div>
                          </div>
                        </Link>
                        <Link
                          to="/leistungen/webdesign"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-layout-masonry-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              Webdesign & Conversion
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              High-End Corporate Sites & Sales-Funnels
                            </div>
                          </div>
                        </Link>
                        <Link
                          to="/leistungen/reputation-listing"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-star-smile-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              Reputation & Local SEO
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              Markenvertrauen & Top-3 Sichtbarkeit
                            </div>
                          </div>
                        </Link>
                      </div>

                      {/* Column 3: Strategie, BI & Förderung */}
                      <div className="space-y-3">
                        <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-400 px-3">
                          03 // Transformation & BI
                        </div>
                        <Link
                          to="/leistungen/digitale-transformation"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-global-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              Digitale Transformation
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              Cloud-Architektur & API-Ökosysteme
                            </div>
                          </div>
                        </Link>
                        <Link
                          to="/leistungen/business-intelligence"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-bar-chart-box-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              Business Intelligence
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              Echtzeit-KPI-Dashboards & Predictive Analytics
                            </div>
                          </div>
                        </Link>
                        <Link
                          to="/leistungen/foerderung"
                          className="flex items-start gap-3.5 p-3.5 rounded-2xl hover:bg-slate-100/80 dark:hover:bg-white/[0.05] border border-transparent hover:border-slate-200/80 dark:hover:border-white/[0.08] transition-all group/item"
                        >
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 group-hover/item:bg-emerald-500 group-hover/item:text-white text-emerald-600 dark:text-emerald-400 transition-colors">
                            <i className="ri-funds-box-line text-lg" />
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-slate-900 dark:text-white group-hover/item:text-emerald-600 dark:group-hover/item:text-emerald-400 transition-colors">
                              Staatliche Fördermittel
                            </div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              Bis zu 50–80% Zuschuss (BAFA / go-digital)
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>

                    {/* Mega Menu Bottom Bar */}
                    <div className="mt-7 pt-5 border-t border-slate-200/80 dark:border-white/[0.08] flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                      <div className="flex items-center gap-6">
                        <a
                          href="tel:+4970263999988"
                          className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        >
                          <i className="ri-phone-line text-emerald-500" />
                          <span>07026-39999 88</span>
                        </a>
                        <a
                          href="mailto:info@consulting-smc.de"
                          className="flex items-center gap-2 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                        >
                          <i className="ri-mail-line text-emerald-500" />
                          <span>info@consulting-smc.de</span>
                        </a>
                      </div>
                      <Link
                        to="/kontakt"
                        className="inline-flex items-center gap-2 font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 px-4 py-2 rounded-xl shadow-md shadow-emerald-500/20 transition-all"
                      >
                        <i className="ri-calendar-check-line" />
                        <span>Potenzialanalyse vereinbaren</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {[
                { to: '/ueber-uns', label: 'Über uns' },
                { to: '/cases', label: 'Referenzen' },
                { to: '/standorte', label: 'Standorte' },
                { to: '/blog', label: 'Insights & Blog' },
                { to: '/kontakt', label: 'Kontakt' }
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`text-xs xl:text-sm font-semibold transition-all duration-200 px-2 xl:px-3.5 py-2 rounded-xl whitespace-nowrap ${
                    isActive(item.to)
                      ? 'text-emerald-600 dark:text-emerald-400 bg-white dark:bg-slate-800/90 shadow-sm'
                      : 'text-slate-700 dark:text-slate-200 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-white/60 dark:hover:bg-white/[0.05]'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right Actions — Desktop */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0">
              <ThemeToggle />
              <Link
                to="/kontakt"
                className="group relative inline-flex items-center gap-1.5 xl:gap-2 bg-gradient-to-b from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-600 text-white px-3.5 xl:px-5 py-2 xl:py-2.5 rounded-xl text-xs xl:text-sm font-semibold transition-all duration-300 shadow-[0_1px_2px_rgba(0,0,0,0.2),0_8px_20px_-4px_rgba(16,185,129,0.4),inset_0_1px_0_0_rgba(255,255,255,0.25)] hover:shadow-[0_12px_24px_-4px_rgba(16,185,129,0.55)] hover:-translate-y-0.5 whitespace-nowrap"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                <span>Strategiegespräch</span>
                <i className="ri-arrow-right-up-line text-base group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>

            {/* Mobile Menu Trigger & Theme Toggle */}
            <div className="lg:hidden flex items-center gap-2.5">
              <ThemeToggle />
              <button
                onClick={toggleMenu}
                className="relative w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100/90 dark:bg-slate-800/80 border border-slate-200/80 dark:border-white/10 text-slate-800 dark:text-white transition-all z-[90]"
                aria-label="Menü öffnen"
              >
                <div className="w-5 h-5 flex flex-col justify-center items-center">
                  <span
                    className={`block w-4 h-0.5 bg-emerald-600 dark:bg-emerald-400 transition-all duration-300 ${
                      isMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'
                    }`}
                  />
                  <span
                    className={`block w-4 h-0.5 bg-emerald-600 dark:bg-emerald-400 transition-all duration-300 ${
                      isMenuOpen ? 'opacity-0' : 'opacity-100'
                    }`}
                  />
                  <span
                    className={`block w-4 h-0.5 bg-emerald-600 dark:bg-emerald-400 transition-all duration-300 ${
                      isMenuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'
                    }`}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      <div
        className={`fixed inset-0 bg-slate-950/60 backdrop-blur-md z-[70] lg:hidden transition-all duration-500 ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={closeMenu}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-white dark:bg-[#0B1120] border-l border-slate-200/80 dark:border-white/10 shadow-2xl transition-transform duration-500 transform ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Mobile Drawer Header */}
            <div className="flex items-center justify-between p-5 border-b border-slate-200/80 dark:border-white/[0.08]">
              <div className="flex items-center gap-3">
                <img
                  src="/images/ui/smc-consulting-logo.png"
                  alt="SMC Consulting Logo"
                  className="h-9 w-auto"
                />
                <div>
                  <div className="text-base font-bold text-slate-900 dark:text-white">
                    SMC Consulting
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">
                    Executive Digital Transformation
                  </div>
                </div>
              </div>
              <button
                onClick={closeMenu}
                className="w-9 h-9 rounded-xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors"
                aria-label="Menü schließen"
              >
                <i className="ri-close-line text-xl text-slate-600 dark:text-slate-300" />
              </button>
            </div>

            {/* Mobile Links */}
            <nav className="flex-1 overflow-y-auto p-5 space-y-2">
              <div className="space-y-1.5 pb-3 border-b border-slate-200/70 dark:border-white/[0.08]">
                <Link
                  to="/leistungen"
                  onClick={closeMenu}
                  className="flex items-center justify-between p-3 rounded-xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold text-sm"
                >
                  <span className="flex items-center gap-2.5">
                    <i className="ri-cpu-line text-base" />
                    Leistungen & Lösungen
                  </span>
                  <i className="ri-arrow-right-s-line" />
                </Link>
                <div className="pl-3 space-y-1">
                  {[
                    { to: '/leistungen/ki-automatisierung', icon: 'ri-cpu-line', label: 'KI-Automatisierung' },
                    { to: '/leistungen/ki-chatbots', icon: 'ri-robot-2-line', label: 'KI-Chatbots & Voice AI' },
                    { to: '/leistungen/softwareentwicklung', icon: 'ri-code-s-slash-line', label: 'Softwareentwicklung' },
                    { to: '/leistungen/webdesign', icon: 'ri-layout-masonry-line', label: 'Webdesign & Conversion' },
                    { to: '/leistungen/digitale-transformation', icon: 'ri-global-line', label: 'Digitale Transformation' },
                    { to: '/leistungen/business-intelligence', icon: 'ri-bar-chart-box-line', label: 'Business Intelligence' },
                    { to: '/leistungen/reputation-listing', icon: 'ri-star-smile-line', label: 'Reputationsmanagement' },
                    { to: '/leistungen/foerderung', icon: 'ri-funds-box-line', label: 'Fördermittelberatung' }
                  ].map((sub) => (
                    <Link
                      key={sub.to}
                      to={sub.to}
                      onClick={closeMenu}
                      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-white/[0.05] text-slate-600 dark:text-slate-300 text-sm transition-colors"
                    >
                      <i className={`${sub.icon} text-emerald-500`} />
                      <span>{sub.label}</span>
                    </Link>
                  ))}
                </div>
              </div>

              {[
                { to: '/ueber-uns', icon: 'ri-building-line', label: 'Über uns' },
                { to: '/cases', icon: 'ri-briefcase-4-line', label: 'Referenzen & Cases' },
                { to: '/standorte', icon: 'ri-map-pin-2-line', label: 'Standorte & Regionen' },
                { to: '/blog', icon: 'ri-article-line', label: 'Insights & Blog' },
                { to: '/kontakt', icon: 'ri-calendar-check-line', label: 'Kontakt' }
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={closeMenu}
                  className={`flex items-center gap-3.5 p-3.5 rounded-xl text-sm font-semibold transition-colors ${
                    isActive(link.to)
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/[0.05]'
                  }`}
                >
                  <i className={`${link.icon} text-lg text-emerald-500`} />
                  <span>{link.label}</span>
                </Link>
              ))}
            </nav>

            {/* Mobile Drawer Footer */}
            <div className="p-5 border-t border-slate-200/80 dark:border-white/[0.08] space-y-3">
              <Link
                to="/kontakt"
                onClick={closeMenu}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-3.5 px-5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20"
              >
                <i className="ri-calendar-check-line" />
                <span>Strategiegespräch vereinbaren</span>
              </Link>
              <div className="flex items-center justify-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                <a href="tel:+4970263999988" className="hover:text-emerald-500">
                  07026-39999 88
                </a>
                <span>•</span>
                <a href="mailto:info@consulting-smc.de" className="hover:text-emerald-500">
                  info@consulting-smc.de
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
